import { Helmet } from 'react-helmet-async'
import { FadeIn } from '@components/animations/FadeIn/FadeIn'
import { Heart, Book, Trees, Users, Globe, Hand } from 'lucide-react'

const csrInitiatives = [
  {
    icon: Book,
    title: 'Education for All',
    description: 'Providing digital learning tools and infrastructure to rural schools. Adopted 50+ schools in Maharashtra.',
    impact: '50+ Schools, 10,000+ Students',
  },
  {
    icon: Trees,
    title: 'Environmental Sustainability',
    description: 'Tree plantation drives, e-waste management, and carbon footprint reduction initiatives.',
    impact: '1 Lakh+ Trees Planted',
  },
  {
    icon: Heart,
    title: 'Healthcare Access',
    description: 'Free health camps, medical equipment donations, and health awareness programs in rural areas.',
    impact: '100+ Health Camps',
  },
  {
    icon: Users,
    title: 'Women Empowerment',
    description: 'Skill development programs, digital literacy training, and entrepreneurship support for women.',
    impact: '5,000+ Women Trained',
  },
  {
    icon: Hand,
    title: 'Community Development',
    description: 'Infrastructure development in adopted villages including clean water, sanitation, and digital connectivity.',
    impact: '20+ Villages Adopted',
  },
  {
    icon: Globe,
    title: 'Digital Literacy',
    description: 'Free computer training programs for underprivileged youth and senior citizens.',
    impact: '10,000+ Trained',
  },
]

const CSR = () => {
  return (
    <>
      <Helmet>
        <title>Corporate Social Responsibility | Eonixa Dynamics</title>
        <meta name="description" content="Our CSR initiatives - Education, Environment, Healthcare, Women Empowerment and Community Development" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 pt-32 pb-20">
        <div className="section-container">
          <FadeIn>
            <div className="text-center mb-16">
              <h1 className="text-4xl font-bold text-gray-900">Corporate Social Responsibility</h1>
              <p className="mt-4 text-lg text-gray-600 max-w-3xl mx-auto">
                Giving back to the society that has nurtured our growth.
                Our CSR initiatives focus on education, environment, healthcare, and community development.
              </p>
            </div>
          </FadeIn>

          {/* CSR Stats */}
          <FadeIn delay={0.2}>
            <div className="grid gap-8 sm:grid-cols-4 mb-16">
              <div className="rounded-2xl bg-white p-6 text-center shadow-soft">
                <p className="text-4xl font-bold text-primary-600">₹10Cr+</p>
                <p className="mt-2 text-gray-600">CSR Investment</p>
              </div>
              <div className="rounded-2xl bg-white p-6 text-center shadow-soft">
                <p className="text-4xl font-bold text-primary-600">50K+</p>
                <p className="mt-2 text-gray-600">Lives Impacted</p>
              </div>
              <div className="rounded-2xl bg-white p-6 text-center shadow-soft">
                <p className="text-4xl font-bold text-primary-600">100+</p>
                <p className="mt-2 text-gray-600">Initiatives</p>
              </div>
              <div className="rounded-2xl bg-white p-6 text-center shadow-soft">
                <p className="text-4xl font-bold text-primary-600">20+</p>
                <p className="mt-2 text-gray-600">Partner NGOs</p>
              </div>
            </div>
          </FadeIn>

          {/* CSR Initiatives */}
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {csrInitiatives.map((initiative, index) => {
              const Icon = initiative.icon
              return (
                <FadeIn key={initiative.title} delay={index * 0.1}>
                  <div className="group h-full rounded-2xl bg-white p-8 shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-card">
                    <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-xl bg-primary-50 text-primary-600 group-hover:bg-primary-600 group-hover:text-white transition-colors">
                      <Icon className="h-7 w-7" />
                    </div>
                    <h3 className="mb-3 text-lg font-bold text-gray-900">
                      {initiative.title}
                    </h3>
                    <p className="text-gray-600 mb-4">{initiative.description}</p>
                    <p className="text-sm font-semibold text-primary-600">
                      Impact: {initiative.impact}
                    </p>
                  </div>
                </FadeIn>
              )
            })}
          </div>

          <FadeIn delay={0.6} className="mt-16">
            <div className="rounded-2xl bg-gradient-to-r from-primary-900 to-primary-700 p-12 text-center text-white">
              <h2 className="mb-4 text-2xl font-bold">Our CSR Commitment</h2>
              <p className="mx-auto max-w-2xl text-gray-200">
                We commit 2% of our annual profits to CSR initiatives, focusing on
                sustainable development and creating lasting positive impact in the
                communities we serve.
              </p>
            </div>
          </FadeIn>
        </div>
      </div>
    </>
  )
}

export default CSR
